import { useState, useEffect } from "react";
import {
  ArrowLongLeftIcon,
  ArrowLongRightIcon,
} from "@heroicons/react/20/solid";

export default function Pagination({
  prevPage,
  nextPage,
  totalPages,
  setPage,
  currentPage: externalCurrentPage,
}) {
  const [currentPage, setCurrentPage] = useState(externalCurrentPage || 1);

  // Sync internal state with external currentPage prop
  useEffect(() => {
    if (externalCurrentPage) {
      setCurrentPage(externalCurrentPage);
    }
  }, [externalCurrentPage]);

  const handlePrevClick = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      prevPage();
    }
  };

  const handleNextClick = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      nextPage();
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
    setPage(pageNumber);
    // You might want to call a function here to fetch data for the specific page
  };

  const generatePageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pageNumbers.push(1, 2, 3, 4, 5, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pageNumbers.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    return pageNumbers;
  };

  const handleEllipsisClick = (side) => {
    if (side === 'left') {
      const newPage = Math.max(currentPage - 2, 1);
      setCurrentPage(newPage);
      setPage(newPage);
    } else if (side === 'right') {
      const newPage = Math.min(currentPage + 2, totalPages);
      setCurrentPage(newPage);
      setPage(newPage);
    }
  };

  const pages = generatePageNumbers();

  return (
    <nav className="flex items-center justify-center border-t border-gray-200 px-4 sm:px-0 my-9">
      <div className="-mt-px flex w-0 flex-1">
        <a
          href="#"
          onClick={handlePrevClick}
          className={`inline-flex items-center border-t-2 border-transparent pr-1 pt-4 text-sm font-medium ${
            currentPage === 1
              ? "text-gray-300"
              : "text-gray-500 hover:border-gray-300 hover:text-gray-700"
          }`}
        >
          <ArrowLongLeftIcon className="mr-3 h-5 w-5 text-gray-400" aria-hidden="true" />
        </a>
      </div>
      <div className="hidden md:-mt-px md:flex">
        {pages.map((page, index) => (
          <span key={index}>
            {page === '...' ? (
              <span
                className="inline-flex items-center border-t-2 px-4 pt-4 text-sm font-medium text-gray-500 cursor-pointer"
                onClick={() => handleEllipsisClick(index < pages.indexOf(currentPage) ? 'left' : 'right')}
              >
                ...
              </span>
            ) : (
              <a
                href="#"
                onClick={() => handlePageClick(page)}
                className={`inline-flex items-center border-t-2 px-4 pt-4 text-sm font-medium ${
                  currentPage === page
                    ? "border-purple-400 text-[#895a7c]"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
                aria-current={currentPage === page ? "page" : undefined}
              >
                {page}
              </a>
            )}
          </span>
        ))}
      </div>
      <div className="-mt-px flex w-0 flex-1 justify-end">
        <a
          href="#"
          onClick={handleNextClick}
          className={`inline-flex items-center border-t-2 border-transparent pl-1 pt-4 text-sm font-medium ${
            currentPage === totalPages
              ? "text-gray-300"
              : "text-gray-500 hover:border-gray-300 hover:text-gray-700"
          }`}
        >
          <ArrowLongRightIcon className="ml-3 h-5 w-5 text-gray-400" aria-hidden="true" />
        </a>
      </div>
    </nav>
  );
}
